// Shared read-only mode for the page views that the public /demo reuses.
//
// The signed-in app renders these views normally. The demo renders the very
// same components inside <ReadOnlyProvider>, which turns every navigation and
// every mutating control into inert chrome that still looks exactly like the
// product.

import { createContext, useContext, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";

const ReadOnlyContext = createContext(false);

export function ReadOnlyProvider({ children }: { children: ReactNode }) {
  return <ReadOnlyContext.Provider value={true}>{children}</ReadOnlyContext.Provider>;
}

export function useReadOnly() {
  return useContext(ReadOnlyContext);
}

/**
 * Renders a real router link in the app and a visually identical, inert span
 * in the demo. Keeps hover styling so the layout never shifts between the two.
 * Props are passed straight through to Link; typing is intentionally loose so
 * call sites keep the shapes they already use.
 */
export function ViewLink(
  props: { to?: string; children?: ReactNode; className?: string } & Record<string, any>,
) {
  const readOnly = useReadOnly();
  if (!readOnly) return <Link {...(props as any)} />;
  if (props.to === "/activation") return <Link to="/check" className={props.className}>Check my site</Link>;
  const { className, children, ...rest } = props as any;
  void rest;
  return (
    <span className={className} aria-disabled="true">
      {children}
    </span>
  );
}
